import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Select } from "../../../../components/Form/Input/select";
import Label from "../../../../components/Form/Label";
import { Label as LabelText } from "../../../../components/Form/Label/styles";
import { FormGroup } from "../../../../components/Form/styles";
import { OptionTiles } from "../../../../components/OptionTiles";
import { useSite } from "../../../../contexts/Site";
import { getPages } from "../../../../model/pages";

const Wrapper = styled.div`
  margin-bottom: 1rem;
`;

/* Takes the room of the text fields around it (48px tall, 2px more than the Select's own 8px above and below it),
   so switching to it doesn't move what's under */
const PageSelect = styled(Select)`
  height: 3rem;
  margin: 10px 0;
`;

const icon = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8 };

const LinkIcon = () => (
  <svg {...icon} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
  </svg>
);

const PageIcon = () => (
  <svg {...icon} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
    <path d="M14 3v5h5" />
  </svg>
);

/** `pageId` when it can still be linked to (a page of this Keepo); else undefined. */
export const useLinkablePageId = (pageId?: string): string | undefined => {
  const { site } = useSite();
  return getPages(site).some(({ id }) => id === pageId) ? pageId : undefined;
};

type Props = {
  pageId?: string;
  onChange: (pageId: string | undefined) => void;
};

/** "Where it goes": an address, or a page of this Keepo (when it has pages). Saves the page's id, not its slug. */
export const PageLinkField = ({ pageId, onChange }: Props) => {
  const { t } = useTranslation();
  const { site } = useSite();
  const pages = getPages(site);
  const [toPage, setToPage] = useState(!!pageId);
  const prefix = "pages.editor.pageLink";

  // kept while the link still names a page (deleted), so it can go back to an address
  if (!pages.length && !pageId) {
    return null;
  }

  const selected = pages.some(({ id }) => id === pageId) ? pageId : "";

  return (
    <>
      <Wrapper>
        <LabelText as="p" id="page-link-label">
          {t(`${prefix}.label`)}
        </LabelText>
        <OptionTiles $columns={2} $compact role="radiogroup" aria-labelledby="page-link-label">
          <button
            type="button"
            role="radio"
            aria-checked={!toPage}
            onClick={() => {
              setToPage(false);
              onChange(undefined);
            }}>
            <LinkIcon />
            {t(`${prefix}.url`)}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={toPage}
            onClick={() => {
              // never to a page without one: the address field (hidden by a page) and Save follow it
              setToPage(true);
              if (!selected && pages.length) onChange(pages[0].id);
            }}>
            <PageIcon />
            {t(`${prefix}.page`)}
          </button>
        </OptionTiles>
      </Wrapper>
      {toPage && (
        <FormGroup>
          <PageSelect id="page-link-select" value={selected} onChange={e => onChange(e.target.value || undefined)}>
            {/* only for a link to a page that's gone: pick another one (or go back to an address) */}
            {!selected && <option value="">{t(`${prefix}.choose`)}</option>}
            {pages.map(page => (
              <option key={page.id} value={page.id}>
                {page.title}
              </option>
            ))}
          </PageSelect>
          <Label htmlFor="page-link-select" text={t(`${prefix}.choose`)} />
        </FormGroup>
      )}
    </>
  );
};
